import { Plus, Search, X } from "lucide-react";
import {
  useGetMany,
  useRecordContext,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { useTags } from "../tags/useTags";
import type { Contact, Tag } from "../types";

// Dimension display order — matches PRD §9
const DIMENSION_ORDER = [
  "客户来源",
  "采购行为",
  "跟进策略",
  "合作特点",
  "地区市场",
  "风险预警",
  "沟通偏好",
];

export const TagsListEdit = () => {
  const record = useRecordContext<Contact>();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data: allTags, isPending: isPendingAllTags } = useTags({
    perPage: 200,
  });
  const { data: selectedTags, isPending: isPendingRecordTags } =
    useGetMany<Tag>("tags", { ids: record?.tags ?? [] }, { enabled: !!record });
  const [update] = useUpdate<Contact>();

  const selectedIds: Set<Identifier> = useMemo(
    () => new Set(record?.tags ?? []),
    [record?.tags],
  );

  // Group allTags by category, respecting DIMENSION_ORDER
  const dimensionGroups = useMemo(() => {
    if (!allTags) return [];
    const lowerSearch = search.toLowerCase();

    const filtered = allTags.filter(
      (tag) =>
        !search ||
        tag.name.toLowerCase().includes(lowerSearch) ||
        (tag.category ?? "").toLowerCase().includes(lowerSearch),
    );

    const grouped: { dimension: string; tags: Tag[] }[] = [];
    for (const dim of DIMENSION_ORDER) {
      const tags = filtered.filter((t) => t.category === dim);
      if (tags.length > 0) grouped.push({ dimension: dim, tags });
    }
    // Uncategorized tags
    const uncategorized = filtered.filter(
      (t) => !t.category || !DIMENSION_ORDER.includes(t.category),
    );
    if (uncategorized.length > 0)
      grouped.push({ dimension: "其他", tags: uncategorized });
    return grouped;
  }, [allTags, search]);

  const toggleTag = (id: Identifier) => {
    if (!record) return;
    const next = selectedIds.has(id)
      ? record.tags.filter((tid) => tid !== id)
      : [...(record.tags ?? []), id];
    update("contacts", { id: record.id, data: { tags: next }, previousData: record });
  };

  // Group selected tags by dimension
  const selectedByDimension = useMemo(() => {
    if (!selectedTags) return [];
    const groups: { dimension: string; tags: Tag[] }[] = [];
    for (const dim of DIMENSION_ORDER) {
      const tags = selectedTags.filter((t) => t.category === dim);
      if (tags.length > 0) groups.push({ dimension: dim, tags });
    }
    const uncategorized = selectedTags.filter(
      (t) => !t.category || !DIMENSION_ORDER.includes(t.category),
    );
    if (uncategorized.length > 0)
      groups.push({ dimension: "其他", tags: uncategorized });
    return groups;
  }, [selectedTags]);

  if (isPendingRecordTags || isPendingAllTags) return null;

  return (
    <div className="space-y-3">
      {/* Selected tags grouped by dimension */}
      {selectedByDimension.length > 0 ? (
        selectedByDimension.map(({ dimension, tags }) => (
          <div key={dimension}>
            <p className="text-xs text-muted-foreground font-medium mb-1.5">
              {dimension}
            </p>
            <div className="flex flex-wrap gap-1.5 pl-1">
              {tags.map((tag) => (
                <Badge
                  key={tag.id}
                  variant="secondary"
                  className="text-sm md:text-xs font-normal text-black pr-1 gap-1"
                  style={{ backgroundColor: tag.color }}
                >
                  {tag.name}
                  <button
                    className="ml-0.5 rounded-full hover:bg-black/10 p-0.5 cursor-pointer"
                    onClick={() => toggleTag(tag.id)}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        ))
      ) : (
        <p className="text-xs text-muted-foreground">暂无标签</p>
      )}

      {/* Tag picker popover */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-9 md:h-6 cursor-pointer"
          >
            <Plus className="w-4 h-4 md:w-3 md:h-3 mr-1" />
            添加标签
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-72 p-0" align="start">
          {/* Search */}
          <div className="flex items-center border-b px-3 py-2 gap-2">
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <Input
              placeholder="搜索标签..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-7 border-0 p-0 shadow-none focus-visible:ring-0 text-sm"
            />
          </div>

          {/* Dimension groups */}
          <div className="max-h-80 overflow-y-auto py-1">
            {dimensionGroups.length === 0 && (
              <p className="text-sm text-muted-foreground px-3 py-4 text-center">
                没有匹配的标签
              </p>
            )}
            {dimensionGroups.map(({ dimension, tags }) => (
              <div key={dimension} className="px-2 py-1">
                <p className="text-xs text-muted-foreground font-medium px-1 py-1">
                  {dimension}
                </p>
                <div className="flex flex-wrap gap-1">
                  {tags.map((tag) => {
                    const active = selectedIds.has(tag.id);
                    return (
                      <Badge
                        key={tag.id}
                        variant="secondary"
                        className="text-xs font-normal cursor-pointer select-none transition-opacity"
                        title={tag.description}
                        style={{
                          backgroundColor: tag.color,
                          opacity: active ? 1 : 0.55,
                          outline: active ? `2px solid ${tag.color}` : "none",
                          outlineOffset: "1px",
                        }}
                        onClick={() => toggleTag(tag.id)}
                      >
                        {active && <span className="mr-0.5">✓</span>}
                        {tag.name}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
